/**
 * PortalSubscription — what I have and until when (Stage 3.4, 29 Sep 2026).
 * Reads users/{uid} live; "Manage subscription" opens Stripe's customer portal (createPortalSession).
 * Shows: plan, billing period, status (trial / active / payment problem / ending / ended), free access from an admin
 * grant (e.g. sticker program, interns), and the products the account can use now.
 *
 * Props: user (signed-in Firebase user), onChoosePlan () => void
 */
import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase-config';
import cfg from './plans.json';
import { PRODUCT_NAMES } from './PortalPlans';
import './PortalPlans.css';

const fmt = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const toMs = (t) => (t?.toMillis ? t.toMillis() : t ? new Date(t).getTime() : 0);

const STATUS = {
  trialing: { icon: '🎁', text: 'Free trial', tone: 'ok' },
  active: { icon: '✓', text: 'Active', tone: 'ok' },
  past_due: { icon: '⚠️', text: 'Payment problem — please update your card', tone: 'warn' },
  unpaid: { icon: '⚠️', text: 'Unpaid — access paused', tone: 'bad' },
  incomplete: { icon: '⚠️', text: 'Payment not completed (card declined?) — choose a plan again under Plans', tone: 'warn' },
  incomplete_expired: { icon: '✕', text: 'Payment not completed — choose a plan again under Plans', tone: 'bad' },
  canceled: { icon: '✕', text: 'Ended', tone: 'bad' },
};

const PortalSubscription = ({ user, onChoosePlan }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const justPaid = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('checkout') === 'success';

  useEffect(() => {
    if (!user) return undefined;
    return onSnapshot(doc(db, 'users', user.uid), (s) => setData(s.data() || {}));
  }, [user]);

  const manage = async () => {
    setBusy(true); setError('');
    try {
      const res = await httpsCallable(getFunctions(), 'createPortalSession')();
      window.location.href = res.data.url;
    } catch (e) { setError(e.message || 'Could not open subscription management.'); setBusy(false); }
  };

  if (!user) return <div className="pp-page"><p>Please sign in.</p></div>;
  if (!data) return <div className="pp-page"><p>Loading…</p></div>;

  const sub = data.portalSubscription;
  const plan = sub?.plan ? cfg.plans.find((p) => p.id === sub.plan) : null;
  const st = sub ? STATUS[sub.status] || { icon: '•', text: sub.status, tone: 'warn' } : null;
  const grant = data.accessGrant && (!data.accessGrant.until || toMs(data.accessGrant.until) > Date.now()) ? data.accessGrant : null;
  const products = data.products || [];

  return (
    <div className="pp-page pp-narrow">
      <div className="pp-header"><h1>Subscription</h1></div>

      {justPaid && !sub && <div className="pp-note">Payment received — activating your account… (a few seconds)</div>}

      {sub ? (
        <div className="pp-panel">
          <div className="pp-row"><span>Plan</span><strong>{plan?.name || sub.plan}</strong></div>
          <div className="pp-row"><span>Billing</span><strong>{cfg.periods[sub.period]?.label || sub.period}</strong></div>
          <div className={`pp-row pp-${st.tone}`}><span>Status</span><strong>{st.icon} {st.text}</strong></div>
          {sub.status === 'trialing' && sub.trialEnd && (
            <div className="pp-row"><span>Trial ends</span><strong>{fmt(sub.trialEnd)}</strong></div>
          )}
          {['active', 'trialing', 'past_due'].includes(sub.status) && sub.currentPeriodEnd && (
            <div className="pp-row"><span>{sub.cancelAtPeriodEnd ? 'Ends on' : 'Renews on'}</span><strong>{fmt(sub.currentPeriodEnd)}</strong></div>
          )}
          <button className="pp-btn" disabled={busy} onClick={manage}>
            {busy ? 'Opening…' : 'Manage subscription (plan, card, invoices, cancel)'}
          </button>
        </div>
      ) : (
        <div className="pp-panel">
          <p>No paid subscription.</p>
          <button className="pp-btn" onClick={() => onChoosePlan?.()}>See plans</button>
        </div>
      )}

      {grant && (
        <div className="pp-panel">
          <div className="pp-row"><span>Free access</span>
            <strong>{(grant.products || []).map((p) => PRODUCT_NAMES[p] || p).join(', ')}</strong></div>
          <div className="pp-row"><span>Until</span><strong>{grant.until ? fmt(toMs(grant.until)) : 'no end date'}</strong></div>
          {grant.note && <div className="pp-row"><span>Note</span><strong>{grant.note}</strong></div>}
        </div>
      )}

      <div className="pp-panel">
        <div className="pp-row"><span>You can use now</span>
          <strong>{products.length ? products.map((p) => PRODUCT_NAMES[p] || p).join(', ') : 'nothing yet'}</strong></div>
      </div>

      {error && <div className="pp-error">{error}</div>}
    </div>
  );
};

export default PortalSubscription;
